import BigNumber from 'bignumber.js';
import { ActionCableService, distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from 'ngx-libs';
import { combineLatest, forkJoin, NEVER, Observable, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { merge, pick } from 'lodash';
import { Base } from '../base';
import { OffChain } from './off-chain';
import { AccountService } from 'src/app/services/account.service';
import { ContractService } from 'src/app/services/contract.service';
import { EthWalletService } from 'src/app/services/eth-wallet.service';
import { MainService } from 'src/app/services/main.service';
import { Market } from '../market';
import VaultAbi from 'src/app/abi/vault';
// import { OnChain } from './on-chain';
import { keepUpdater } from 'src/app/rxjs';
import { environment } from 'src/environments/environment';
import { EIP712Signer } from '../eip712-signer';
import { Paymaster } from '../paymaster';
import { Token } from '../token';
import { PaymasterMethod } from '../paymaster-method';
// import { OnChain2 } from './on-chain2';

const greeks = require('greeks');

export const MAX_INT = new BigNumber('57896044618658097711785492504343953926634992332820282019728792003956564819967');

const DECIMALS18 = new BigNumber(10).pow(18);
const POSITION_FIXED_KEYS = ['expiry', 'strike', 'isCall', 'size', 'notional'];

export interface Trade {
  expiry: number;
  strike: any;
  isCall: boolean;
  amount: any;
  acceptableTotal: any;
}

export class Vault extends Base {
  quoteAmount: Observable<BigNumber>;
  quoteApproved: Observable<boolean>;
  quoteAllowance: Observable<BigNumber>;
  marginBalance: Observable<BigNumber>;
  equity: Observable<BigNumber>;
  // private upnl: Observable<BigNumber>;
  available: Observable<BigNumber>;
  // private initialMargin: Observable<BigNumber>;
  healthFactor: Observable<number>;
  withdrawable: Observable<BigNumber>;

  positions: Observable<any[]>;
  now: Observable<number>;
  tradeDisabled: Observable<boolean>;
  myNetDelta: Observable<BigNumber>;
  liquidatePrices: Observable<BigNumber[]>;

  // onChain: OnChain;
  // onChain2: OnChain2;
  offChain: OffChain;
  chain: OffChain;

  constructor(
    protected contract: string,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    private market: Market
  ) {
    super(contract, VaultAbi, accountService, contractService);
    this.offChain = new OffChain(
      contract,
      accountService,
      contractService,
      ethWalletService,
      actionCableService,
      mainService,
      market
    );
    // this.onChain = new OnChain(contract, accountService, contractService, ethWalletService, mainService, market);
    // this.onChain2 = new OnChain2(contract, accountService, contractService, ethWalletService, mainService, market);
    this.chain = this.offChain;

    // this.quoteAmount = this.ethWalletService.blockNumber.pipe(
    //   keepUpdater(),
    //   switchMap(() => this.market.collateral.balanceOf(this.contract)),
    //   sharePublishReplay(1)
    // );
    this.quoteAmount = this.ethWalletService.blockNumber.pipe(
      switchMap(() => this.market.collateral.balanceOf(this.contract)),
      sharePublishReplay(1)
    );

    let approved = false;
    const account = this.accountService.account.pipe(
      distinctUntilChanged(),
      tap(() => {
        approved = false;
      })
    );

    this.quoteAllowance = account.pipe(
      switchMap((a) =>
        timer(0, 5000).pipe(
          filter(() => !approved),
          switchMap(() =>
            a ? this.market.collateral.allowance(a, this.contract).pipe(swalloError()) : of(new BigNumber(0))
          )
        )
      ),
      distinctBigNumberUntilChanged()
    );

    this.quoteApproved = this.quoteAllowance.pipe(
      map((v) => v.gt(0)),
      tap((r) => {
        approved = r;
      }),
      sharePublishReplay(1)
    );

    const balance = this.accountService.account.pipe(
      switchMap((a) => (a ? this.chain.getBalanceOfCache(a) : of(new BigNumber(0))))
    );

    const accountInfo = combineLatest([this.accountService.account, balance]).pipe(
      debounceTime(10),
      switchMap(([a]) => (a ? this.getAccountInfo(a) : of({} as any))),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      sharePublishReplay(1)
    );

    this.marginBalance = accountInfo.pipe(
      map((a) => a.marginBalance || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.equity = accountInfo.pipe(
      map((a) => a.equity || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    // this.upnl = accountInfo.pipe(
    //   map((a) => a.upnl || new BigNumber(0)),
    //   distinctBigNumberUntilChanged()
    // );

    this.available = accountInfo.pipe(
      map((a) => a.available || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.withdrawable = this.available.pipe(
      map((a) => BigNumber.max(a, new BigNumber(0))),
      distinctBigNumberUntilChanged()
    );

    // this.initialMargin = accountInfo.pipe(
    //   map((a) => a.initialMargin || new BigNumber(0)),
    //   distinctBigNumberUntilChanged()
    // );

    this.healthFactor = accountInfo.pipe(
      map((a) => {
        if (a.healthFactor) {
          return a.healthFactor.toDecimal().eq(MAX_INT) ? null : a.healthFactor.toNumber();
        } else {
          return null;
        }
      }),
      distinctUntilChanged()
    );

    this.liquidatePrices = this.accountService.account.pipe(
      switchMap((account) =>
        account
          ? combineLatest([spotPrice, accountInfo]).pipe(
              debounceTime(10),
              filter(([s, a]) => a.healthFactor),
              map(([s, a]) => {
                let upper = s;
                let lower = s;
                if (a.healthFactor.gt(0.5)) {
                  upper = this.findLiquidatePrice(account, s, s.times(11), true);
                  lower = this.findLiquidatePrice(account, new BigNumber(0), s, false);
                  if (upper.gte(s.times(10))) {
                    upper = null;
                  }
                  if (lower.lte(s.div(10))) {
                    lower = null;
                  }
                }
                return [lower, upper];
              })
            )
          : of([])
      )
    );

    this.positions = this.accountService.account.pipe(
      switchMap((a) => (a ? this.chain.getPositionsCache(a) : of([]))),
      map((positions) => positions.map((p: any) => this.processPosition(p))),
      distinctUntilChanged((a, b) => {
        a = a.map((aa: any) => pick(aa, POSITION_FIXED_KEYS));
        b = b.map((bb: any) => pick(bb, POSITION_FIXED_KEYS));
        return JSON.stringify(a) === JSON.stringify(b);
      }),
      sharePublishReplay(1)
    );

    const spotPrice = this.market.spotPricer.price;
    this.myNetDelta = combineLatest([this.positions, spotPrice, this.equity]).pipe(
      map(([positions, sp]) => {
        const spot = sp.toNumber();
        return new BigNumber(
          positions.reduce((netDelta, position) => {
            const iv = this.market.contractVault.optionMarket
              .getIv(position.expiry, position.strike.toDecimal(), position.isCall, position.size.gt(0))
              .fromDecimal()
              .toNumber();
            const time = (position.expiry - Date.now() / 1000) / 31536000;
            const delta = greeks.getDelta(
              spot,
              position.strike.toNumber(),
              time,
              iv,
              0.06,
              position.isCall ? 'call' : 'put'
            );
            return delta * position.size.toNumber() + netDelta;
          }, 0)
        );
      }),
      sharePublishReplay(1)
    );

    const timestamp = this.ethWalletService.blockNumber.pipe(
      switchMap(() => this.getTimestamp()),
      distinctUntilChanged(),
      sharePublishReplay(1)
    );

    this.now = combineLatest([timestamp, timer(0, 1000)]).pipe(map(([t]) => (t ? t : Math.floor(Date.now() / 1000))));
  }

  simulatePositionChange(
    account: string,
    expiry: number,
    strike: BigNumber,
    isCall: boolean,
    sizeChange: BigNumber,
    notionalChange: BigNumber,
    fee: BigNumber
  ) {
    account = account.toLowerCase();
    const position = this.market.contractVault.ledger.positionOf(account, expiry, strike, isCall);
    const balance = this.market.contractVault.ledger.balanceOf(account);
    this.market.contractVault.ledger.addPosition(
      account,
      expiry,
      strike,
      isCall,
      position.size.plus(sizeChange),
      position.notional.plus(notionalChange)
    );
    this.market.contractVault.ledger.balances[account] = balance.plus(fee);
    const accountInfo = this.market.contractVault.getAccountInfo(account);
    const positions = [...(this.market.contractVault.ledger.positionsSnapshots[account] || [])];
    positions.push({
      expiry,
      isCall,
      strike: strike.fromDecimal(),
      size: sizeChange.fromDecimal()
    });
    this.market.contractVault.ledger.restorePositions(account);
    this.market.contractVault.ledger.balances[account] = balance;
    return [accountInfo, positions];
  }

  simulateSpotChange(account: string, spot: BigNumber) {
    account = account.toLowerCase();
    const origSpot = this.market.contractVault.spotPricer.price;
    this.market.contractVault.spotPricer.price = spot;
    const accountInfo = this.market.contractVault.getAccountInfo(account);
    this.market.contractVault.spotPricer.price = origSpot;
    return accountInfo;
  }

  findLiquidatePrice(account: string, from: BigNumber, to: BigNumber, raising: boolean) {
    let price = from.plus(to).div(2);
    for (let i = 0; i < 10; ++i) {
      const sa = this.simulateSpotChange(account, price.toDecimal());
      const hf = sa.healthFactor.fromDecimal();
      if (hf.gt(0.51)) {
        if (raising) {
          from = price;
        } else {
          to = price;
        }
        price = from.plus(to).div(2);
      } else if (hf.lt(0.49)) {
        if (raising) {
          to = price;
        } else {
          from = price;
        }
        price = from.plus(to).div(2);
      } else {
        break;
      }
    }
    return price;
  }

  approveQuote(paymasterMethod?: PaymasterMethod) {
    return this.market.collateral.approveAll(this.contract, paymasterMethod);
  }

  settle(expiry: number, account?: string) {
    return this.send((i, a) => i.methods.settle(account || a, expiry));
  }

  tryDeposit(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.estimateGas((i) => i.methods.deposit(new BigNumber(amount).times(DECIMALS18).toString(10)), {
      paymasterMethod
    });
  }

  deposit(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.deposit(new BigNumber(amount).times(DECIMALS18).toString(10)), {
      paymasterMethod
    });
  }

  withdraw(amount: any, paymasterMethod?: PaymasterMethod) {
    if (environment.features.signedData) {
      return this.market.api.oracles
        .getSignedData()
        .pipe(
          mergeMap((json) =>
            this.send((i) => i.methods.withdraw(new BigNumber(amount).times(DECIMALS18).toString(10)), {
              paymasterMethod,
              extraData: json.data
            })
          )
        );
    } else {
      return this.send((i) => i.methods.withdraw(new BigNumber(amount).times(DECIMALS18).toString(10)), {
        paymasterMethod
      });
    }
  }

  tryWithdraw(amount: any, paymasterMethod?: PaymasterMethod) {
    if (environment.features.signedData) {
      return this.market.signedData.pipe(
        take(1),
        mergeMap((json) =>
          this.estimateGas((i) => i.methods.withdraw(new BigNumber(amount).times(DECIMALS18).toString(10)), {
            paymasterMethod,
            extraData: json.data
          })
        )
      );
    } else {
      return this.estimateGas((i) => i.methods.withdraw(new BigNumber(amount).times(DECIMALS18).toString(10)), {
        paymasterMethod
      });
    }
  }

  getMarket(expiry: number, strike: any, isCall: boolean) {
    return this.call((i) =>
      i.methods.markets(expiry, new BigNumber(strike).times(DECIMALS18).toString(10), isCall)
    ).pipe(
      map((data: any) => {
        return {
          iv: new BigNumber(data.iv).fromDecimal(),
          size: new BigNumber(data.size).fromDecimal(),
          disabled: data.disabled
        };
      })
    );
  }

  getStrikeInfo(expiry: number, strike: any) {
    return forkJoin([this.getMarket(expiry, strike, true), this.getMarket(expiry, strike, false)]).pipe(
      map(([call, put]) => {
        return {
          expiry,
          call,
          put,
          strike: new BigNumber(strike)
        };
      })
    );
  }

  getAccountInfo(account: string) {
    account = account.toLowerCase();
    return this.chain.getAccountInfoCache(account).pipe(map((v: any) => this.processAccountInfo(v)));
  }

  setTimestamp(timestamp: number) {
    return this.send((i) => i.methods.setTimestamp(timestamp));
  }

  getTimestamp() {
    return of(0);
    // return of(1657360417);
    // return this.send((i) => i.methods.setTimestamp(
    //   timestamp
    // ));
  }

  signTrade(trades: Trade[], gasFee: BigNumber = null, deadline = Math.floor(Date.now() / 1000) + 300, subsidyVoucherParams: any = null) {
    const data = this.tradesToData(trades);
    return this.accountService.account.pipe(
      take(1),
      mergeMap((account) => {
        const gasFeeObservable = gasFee
          ? of(gasFee.toDecimal().toString(10))
          : this.market.api.tradeSignatures
              .getGas({ data: { data }, deadline, account })
              .pipe(map((json: any) => json.gas));
        return combineLatest([
          gasFeeObservable,
          this.market.api.tradeSignatures.getNonce(account.toLowerCase())
        ]).pipe(
          take(1),
          mergeMap(([gasFee2, nonceJson]: [any, any]) => {
            const nonce = nonceJson.nonce;
            const message = { data, deadline, gasFee: gasFee2, nonce };
            const types = this.createTypes({
              Trade: [
                { name: 'data', type: 'int256[]' },
                { name: 'deadline', type: 'uint256' },
                { name: 'gasFee', type: 'uint256' },
                { name: 'nonce', type: 'uint256' }
              ]
            });
            account = account.toLowerCase();

            const msgParams = {
              types,
              domain: this.market.domain,
              primaryType: 'Trade',
              message
            };

            return this.ethWalletService.signTypedData(account, msgParams).pipe(
              mergeMap((vrs) =>
                this.market.api.tradeSignatures.create({
                  account,
                  nonce,
                  deadline,
                  ...vrs,
                  gasFee: gasFee2,
                  data: { data },
                  subsidyVoucherParams
                })
              )
            );
          })
        );
      })
    );
  }

  trade(trades: Trade[], deadline = Math.floor(Date.now() / 1000) + 300) {
    if (environment.features.signedData) {
      return this.market.api.oracles
        .getSignedData()
        .pipe(
          mergeMap((json) =>
            this.send((i) => i.methods.trade(this.tradesToData(trades), deadline), { extraData: json.data })
          )
        );
    } else {
      return this.send((i) => i.methods.trade(this.tradesToData(trades), deadline));
    }
  }

  tryTrade(trades: Trade[], deadline = Math.floor(Date.now() / 1000) + 300) {
    if (environment.features.signedData) {
      // return this.market.api.oracles.getSignedData().pipe(
      return this.market.signedData.pipe(
        take(1),
        mergeMap((json) =>
          this.estimateGas((i) => i.methods.trade(this.tradesToData(trades), deadline), { extraData: json.data })
        ),
        catchError((e) => this.tryParseCustomError(e))
      );
    } else {
      return this.estimateGas((i) => i.methods.trade(this.tradesToData(trades), deadline)).pipe(
        catchError((e) => this.tryParseCustomError(e))
      );
    }
  }

  getPremium(expiry: number, strike: BigNumber, isCall: boolean, amount: BigNumber) {
    return this.chain.getPremium(expiry, strike, isCall, amount);
  }

  getStrikeExtra(expiry: any, strike: any, side: string, type: string, amount: BigNumber, totalLiquidity = MAX_INT) {
    amount = side === 'sell' ? amount.times(-1) : amount;
    return this.getPremium(expiry, strike, type === 'call', amount).pipe(
      map((premium) => {
        return {
          premium: premium.premium,
          price: premium.premium.div(amount.abs()).abs()
        };
      })
    );
  }

  getPositions(account: string) {
    return this.chain
      .getPositions(account)
      .pipe(map((positions) => positions.map((p: any) => this.processPosition(p))));
  }

  getPositionsCache(account: string) {
    return this.chain
      .getPositionsCache(account)
      .pipe(map((positions) => positions.map((p: any) => this.processPosition(p))));
  }

  private processPosition(position: any) {
    const value = timer(0, 3000).pipe(
      switchMap(() => this.getPremium(position.expiry, position.strike, position.isCall, position.size.times(-1))),
      map((v) => v.premium),
      sharePublishReplay(1)
    );
    const upnl = value.pipe(map((v) => v.plus(position.notional)));
    return {
      value,
      upnl,
      ...position,
      avg: position.notional.div(position.size).abs()
    };
  }

  getTradeDisabled() {
    return this.call((i) => i.methods.tradeDisabled());
  }

  getDisabledExpiries(expiry: number) {
    return this.call((i) => i.methods.disabledExpiries(expiry));
  }

  private processAccountInfo(accountInfo: any) {
    return {
      initialMargin: new BigNumber(accountInfo.initialMargin).fromDecimal(),
      marginBalance: new BigNumber(accountInfo.marginBalance).fromDecimal(),
      equity: new BigNumber(accountInfo.equity).fromDecimal(),
      equityWithFee: new BigNumber(accountInfo.equityWithFee).fromDecimal(),
      upnl: new BigNumber(accountInfo.upnl).fromDecimal(),
      available: new BigNumber(accountInfo.available).fromDecimal(),
      healthFactor: new BigNumber(accountInfo.healthFactor).fromDecimal()
    };
  }

  tradesToData(trades: Trade[]) {
    let data: any[] = [];
    trades.forEach((t) => {
      data = [
        ...data,
        t.expiry,
        new BigNumber(t.strike).toDecimal().toString(10),
        t.isCall ? 1 : 0,
        new BigNumber(t.amount).toDecimal().toString(10),
        new BigNumber(t.acceptableTotal).toDecimal().toString(10)
      ];
    });
    return data;
  }
}
